
import BrandingConfiguration from './BrandingConfiguration';
import DomainManagement from './DomainManagement';
import FeatureSettings from './FeatureSettings';
import WhiteLabelBilling from './WhiteLabelBilling';
import OAuthConfiguration from './OAuthConfiguration';
import SmtpConfiguration from './SmtpConfiguration';
import Toggle from '../Global/Toggle';
import { StatusBadge } from './WorkflowStates';
import './scss/InstanceManagement.scss';
import Close from '../icons/Close';

/**
 * Instance Management Component
 * Handles the management of active white label instances
 */
const InstanceManagement = ({
  config,
  user,
  activeTab,
  setActiveTab,
  onUpdateConfig,
  onToggleWhiteLabel,
  optimisticStatus,
  toggleLoading,
  saving,
  error,
  setError,
  onCancelSuccess,
  onRefresh
}) => {
  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      icon: 'dashboard'
    },
    {
      id: 'branding',
      label: 'Branding',
      icon: 'palette'
    },
    {
      id: 'domain',
      label: 'Domain',
      icon: 'language'
    },
    {
      id: 'oauth',
      label: 'OAuth',
      icon: 'key'
    },
    {
      id: 'smtp',
      label: 'SMTP',
      icon: 'mail'
    },
    {
      id: 'features',
      label: 'Features',
      icon: 'settings'
    },
    {
      id: 'billing',
      label: 'Billing',
      icon: 'payment'
    }
  ];


  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab config={config} user={user} />;

      case 'branding':
        return (
          <BrandingConfiguration
            config={config.config}
            onUpdate={onUpdateConfig}
            loading={saving}
          />
        );

      case 'domain':
        return (
          <DomainManagement
            config={config.config}
            onUpdate={onUpdateConfig}
            loading={saving}
          />
        );

      case 'oauth':
        return (
          <OAuthConfiguration
            config={config.config}
            onUpdate={onUpdateConfig}
            loading={saving}
          />
        );

      case 'smtp':
        return (
          <SmtpConfiguration
            config={config.config}
            onUpdate={onUpdateConfig}
            loading={saving}
          />
        );

      case 'features':
        return (
          <FeatureSettings
            config={config.config}
            onUpdate={onUpdateConfig}
            loading={saving}
          />
        );

      case 'billing':
        // User has active subscription if they have paid addon OR active trial
        const hasActiveTrial = config?.config?.trial?.isTrialing;
        const hasActiveSubscription = (user?.user?.whiteLabelAddon || hasActiveTrial) && config?.hasWhiteLabel;

        return (
          <WhiteLabelBilling
            hasActiveSubscription={hasActiveSubscription}
            billingInfo={config?.config?.billing}
            usageInfo={config?.config?.usage}
            trialInfo={config?.config?.trial}
            onCancelSuccess={onCancelSuccess}
            onTrialStart={onRefresh}
            onPurchaseSuccess={onRefresh}
          />
        );

      default:
        return <OverviewTab config={config} user={user} />;
    }
  };

  return (
    <div className="instance-management">
      <div className="instance-management__header">
        <div className="header-content">
          <h1>White Label Configuration</h1>
          <p>Manage your branded Zoobbe instance</p>
        </div>

        <div className="header-actions">
          <div className="instance-toggle">
            <div className="toggle-info">
              <span className="toggle-label">Instance Status</span>
              <StatusBadge
                status={optimisticStatus || config?.config?.status || 'inactive'}
                label={toggleLoading ? 'Updating...' : (optimisticStatus || config?.config?.status || 'inactive')}
              />
            </div>
            <Toggle
              checked={optimisticStatus ? optimisticStatus === 'active' : config?.config?.status === 'active'}
              onChange={onToggleWhiteLabel}
              size="md"
              disabled={toggleLoading}
            />
          </div>
        </div>
      </div>

      {/* Status Overview */}
      <div className="status-overview">
        <div className="status-cards">
          <div className="status-card">
            <div className="status-card__icon">
              <span className="material-symbols-outlined">
                workspace_premium
              </span>
            </div>
            <div className="status-card__content">
              <h3>Subscription</h3>
              {config?.config?.trial?.isTrialing ? (
                <div className="trial-status">
                  <StatusBadge status="active" label="Trial Active" />
                  <span className="trial-days">{config.config.trial.daysRemaining} days left</span>
                </div>
              ) : (
                <StatusBadge
                  status={user?.user?.whiteLabelAddon ? 'active' : 'inactive'}
                  label={user?.user?.whiteLabelAddon ? 'Active' : 'Inactive'}
                />
              )}
            </div>
          </div>

          <div className="status-card">
            <div className="status-card__icon">
              <span className="material-symbols-outlined">public</span>
            </div>
            <div className="status-card__content">
              <h3>Instance</h3>
              <StatusBadge
                status={config?.config?.status || 'inactive'}
                label={config?.config?.status || 'Inactive'}
              />
            </div>
          </div>

          {config?.config?.subdomain && (
            <div className="status-card">
              <div className="status-card__icon">
                <span className="material-symbols-outlined">link</span>
              </div>
              <div className="status-card__content">
                <h3>Subdomain</h3>
                <a
                  href={`https://${config.config.subdomain}.zoobbe.com`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="subdomain-link"
                >
                  {config.config.subdomain}.zoobbe.com
                </a>
              </div>
            </div>
          )}

          {config?.config?.trial?.isTrialing ? (
            <div className="status-card">
              <div className="status-card__icon">
                <span className="material-symbols-outlined">schedule</span>
              </div>
              <div className="status-card__content">
                <h3>Trial Ends</h3>
                <span className="billing-date">
                  {new Date(config.config.trial.trialEndDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
            </div>
          ) : config?.config?.billing?.nextBillingDate ? (
            <div className="status-card">
              <div className="status-card__icon">
                <span className="material-symbols-outlined">schedule</span>
              </div>
              <div className="status-card__content">
                <h3>Next Billing</h3>
                <span className="billing-date">
                  {new Date(config.config.billing.nextBillingDate).toLocaleDateString()}
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="instance-management__tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="material-symbols-outlined">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="instance-management__content">
        {renderTabContent()}
      </div>

      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <div className="error-banner__content">
            <span className="material-symbols-outlined">error</span>
            <span className="error-message">{error}</span>
          </div>
          <button
            className="error-banner__close"
            onClick={() => setError(null)}
            aria-label="Close error"
          >
            <Close size={20} />
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Overview Tab Component
 * Shows a summary of the white label instance
 */
const OverviewTab = ({ config }) => {
  return (
    <div className="overview-tab">
      <div className="overview-section">
        <h2>Instance Summary</h2>
        <div className="summary-grid">
          <div className="summary-item">
            <label>Instance Name</label>
            <span>{config?.config?.name || 'Not set'}</span>
          </div>

          <div className="summary-item">
            <label>App Name</label>
            <span>{config?.config?.branding?.appName || 'Zoobbe'}</span>
          </div>

          <div className="summary-item">
            <label>Primary Color</label>
            <div className="color-preview">
              <div
                className="color-swatch"
                style={{ backgroundColor: config?.config?.branding?.primaryColor || '#0966ff' }}
              ></div>
              <span>{config?.config?.branding?.primaryColor || '#0966ff'}</span>
            </div>
          </div>

          <div className="summary-item">
            <label>Public Signup</label>
            <StatusBadge
              status={config?.config?.features?.allowPublicSignup ? 'active' : 'inactive'}
              label={config?.config?.features?.allowPublicSignup ? 'Enabled' : 'Disabled'}
            />
          </div>
        </div>
      </div>

      {config?.config?.usage && (
        <div className="overview-section">
          <h2>Usage Statistics</h2>
          <div className="usage-grid">
            <div className="usage-item">
              <label>Monthly Active Users</label>
              <span className="usage-value">{config.config.usage.monthlyActiveUsers || 0}</span>
            </div>

            <div className="usage-item">
              <label>Storage Used</label>
              <span className="usage-value">{config.config.usage.storageUsed || 0} MB</span>
            </div>

            <div className="usage-item">
              <label>API Calls This Month</label>
              <span className="usage-value">{config.config.usage.apiCallsThisMonth || 0}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstanceManagement;
